import { useEffect, useRef, useState } from "react";
import { AlertCircle, Loader2, MapPin } from "lucide-react";
import { api } from "../lib/api";
import { COUNTRIES } from "../lib/countries";

// Address autocomplete backed by the existing /api/geo/autocomplete proxy
// (Google Places → Photon → Nominatim fallback). No keys in the browser.
// When `country` (ISO-2) is provided, the server restricts suggestions to that
// country; responses from superseded queries are discarded so a slower earlier
// request can never replace newer suggestions.
export default function AddressAutocomplete({ value, onChange, onSelect, country = null,
                                              placeholder = "Search an address or area",
                                              testId = "address-input", disabled = false }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);
  const timer = useRef(null);
  const seq = useRef(0); // monotonically increasing request id — stale-response guard

  const countryName = country ? (COUNTRIES.find(([c]) => c === country)?.[1] || country) : null;

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  // Country switched → any in-flight suggestions belong to the old country.
  useEffect(() => {
    seq.current += 1;
    clearTimeout(timer.current);
    setSuggestions([]);
    setOpen(false);
    setError(null);
    setLoading(false);
  }, [country]);

  const search = (q) => {
    const my = ++seq.current;
    setLoading(true);
    setError(null);
    api.geoAutocomplete(q, null, null, country)
      .then((r) => {
        if (my !== seq.current) return; // a newer query/country superseded this one
        setSuggestions(r.suggestions || []);
        setActive(-1);
        setOpen(true);
      })
      .catch((e) => {
        if (my !== seq.current) return;
        setSuggestions([]);
        setError(e.message || "Address search failed — check your connection and try again.");
        setOpen(true);
      })
      .finally(() => { if (my === seq.current) setLoading(false); });
  };

  const handleChange = (e) => {
    const q = e.target.value;
    onChange(q);
    clearTimeout(timer.current);
    if (q.trim().length < 3) { seq.current += 1; setSuggestions([]); setOpen(false); setLoading(false); setError(null); return; }
    timer.current = setTimeout(() => search(q), 300);
  };

  const pick = (s) => {
    // Country-component validation: if the provider reports a country and it
    // mismatches the selected one, refuse instead of saving inconsistent data.
    if (country && s.country_code && String(s.country_code).toUpperCase() !== country) {
      setError(`That address is in a different country. Change the country above or pick an address in ${countryName}.`);
      setSuggestions([]);
      setOpen(true);
      return;
    }
    setError(null);
    onSelect(s);
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && active >= 0 && suggestions[active]) { e.preventDefault(); pick(suggestions[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative" ref={boxRef} onKeyDown={onKey}>
      <div className="relative">
        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
        {loading && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 animate-spin pointer-events-none" data-testid={`${testId}-loading`} />}
        <input
          data-testid={testId}
          value={value}
          onChange={handleChange}
          onFocus={() => (suggestions.length || error) && setOpen(true)}
          placeholder={placeholder}
          aria-label={placeholder}
          aria-expanded={open}
          aria-autocomplete="list"
          aria-activedescendant={active >= 0 ? `${testId}-option-${active}` : undefined}
          role="combobox"
          className="input-dark !pl-11"
          autoComplete="off"
          disabled={disabled}
        />
      </div>
      {countryName && (
        <p className="mt-1.5 text-[11px] text-zinc-600" data-testid={`${testId}-country-hint`}>
          Suggestions are filtered to <span className="text-zinc-400 font-medium">{countryName}</span> — change the country above to search elsewhere.
        </p>
      )}
      {open && (
        <ul role="listbox" data-testid={`${testId}-suggestions`}
            className="absolute z-40 mt-2 w-full card-dark p-1.5 max-h-64 overflow-auto shadow-2xl animate-[fadeIn_.15s_ease-out]">
          {suggestions.map((s, i) => (
            <li key={s.place_id}>
              <button type="button" role="option" id={`${testId}-option-${i}`} aria-selected={i === active}
                data-testid={`${testId}-option`}
                onMouseEnter={() => setActive(i)}
                className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors ${i === active ? "bg-[#27272A]" : "hover:bg-[#27272A]"}`}
                onClick={() => pick(s)}>
                <span className="block text-sm font-medium text-white line-clamp-1">{s.main_text}</span>
                {s.secondary_text && <span className="block text-xs text-zinc-400 line-clamp-1">{s.secondary_text}</span>}
              </button>
            </li>
          ))}
          {!loading && !error && suggestions.length === 0 && (
            <li className="px-3 py-3 text-xs text-zinc-500" data-testid={`${testId}-empty`}>
              No matching addresses{countryName ? ` in ${countryName}` : ""}. Try a different spelling or check the country.
            </li>
          )}
          {error && (
            <li className="px-3 py-3 text-xs text-amber-400 flex items-start gap-2" role="alert" data-testid={`${testId}-error`}>
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {error}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
